import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "../../hooks/useConfirm";
import { 
  Truck, Plus, Pencil, Trash2, Percent, ToggleLeft, ToggleRight, History, Clock, X
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { ToggleSwitch as Switch } from "../../components/ui/toggle-switch";
import { DateInput } from "../../components/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServiziDelivery = () => {
  const { getAuthHeaders, localeCorrente } = useAuth();
  const confirm = useConfirm();
  const [servizi, setServizi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({
    nome: "",
    commissione_percentuale: "",
    attivo: true
  });
  // Storico commissioni (timeline + correzione)
  const [storicoDialogOpen, setStoricoDialogOpen] = useState(false);
  const [storicoItem, setStoricoItem] = useState(null);
  const [storicoRows, setStoricoRows] = useState([]);
  const [savingStorico, setSavingStorico] = useState(false);

  const formatShort = (iso) => {
    if (!iso) return "-";
    const [y, m, d] = iso.slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
  };
  const oggiISO = () => new Date().toISOString().slice(0, 10);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/servizi-delivery?locale_id=${localeCorrente?.id}`, { headers });
      setServizi(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei servizi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (localeCorrente?.id) {
      fetchData();
    }
  }, [localeCorrente]);

  const openDialog = (item = null) => {
    if (item) {
      setEditItem(item);
      setForm({
        nome: item.nome,
        commissione_percentuale: item.commissione_percentuale.toString(),
        attivo: item.attivo
      });
    } else {
      setEditItem(null);
      setForm({
        nome: "",
        commissione_percentuale: "",
        attivo: true
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.nome.trim()) {
      toast.error("Il nome è obbligatorio");
      return;
    }

    const payload = {
      locale_id: localeCorrente?.id,
      nome: form.nome.trim(),
      commissione_percentuale: parseFloat(form.commissione_percentuale) || 0,
      attivo: form.attivo
    };

    try {
      const headers = getAuthHeaders();
      if (editItem) {
        await axios.put(`${API}/servizi-delivery/${editItem.id}`, payload, { headers });
        toast.success("Servizio aggiornato");
      } else {
        await axios.post(`${API}/servizi-delivery`, payload, { headers });
        toast.success("Servizio creato");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (id) => {
    if (!await confirm("Sei sicuro di voler eliminare questo servizio? I dati delle entrate associati rimarranno ma non saranno più collegati.")) return;

    try {
      await axios.delete(`${API}/servizi-delivery/${id}`, { headers: getAuthHeaders() });
      toast.success("Servizio eliminato");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const toggleAttivo = async (item) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/servizi-delivery/${item.id}`, {
        ...item,
        attivo: !item.attivo
      }, { headers });
      fetchData();
      toast.success(item.attivo ? "Servizio disattivato" : "Servizio attivato");
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const openStorico = (item) => {
    setStoricoItem(item);
    let rows = (item.storico_commissioni || []).map(h => ({ percentuale: h.percentuale.toString(), dal: (h.dal || "").slice(0, 10) }));
    if (rows.length === 0) {
      rows = [{ percentuale: (item.commissione_percentuale ?? 0).toString(), dal: "2000-01-01" }];
    }
    rows.sort((a, b) => a.dal.localeCompare(b.dal));
    setStoricoRows(rows);
    setStoricoDialogOpen(true);
  };

  const updateStoricoRow = (idx, field, value) => {
    setStoricoRows(rows => rows.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };
  const addStoricoRow = () => {
    setStoricoRows(rows => [...rows, { percentuale: "", dal: oggiISO() }]);
  };
  const removeStoricoRow = (idx) => {
    setStoricoRows(rows => rows.filter((_, i) => i !== idx));
  };

  const saveStorico = async () => {
    const parsed = [];
    for (const r of storicoRows) {
      const p = parseFloat(r.percentuale);
      if (isNaN(p) || p < 0) { toast.error("Inserisci una percentuale valida in ogni riga"); return; }
      if (!r.dal || r.dal.length < 10) { toast.error("Inserisci una data valida in ogni riga"); return; }
      parsed.push({ percentuale: p, dal: r.dal.slice(0, 10) });
    }
    if (parsed.length === 0) { toast.error("Serve almeno una percentuale"); return; }
    setSavingStorico(true);
    try {
      await axios.put(`${API}/servizi-delivery/${storicoItem.id}/storico`, { storico_commissioni: parsed }, { headers: getAuthHeaders() });
      toast.success("Storico commissioni aggiornato");
      setStoricoDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio dello storico");
    } finally {
      setSavingStorico(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="servizi-delivery-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Servizi delivery</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Gestisci i servizi di consegna (ogni servizio avrà colonne POS e CASH)
          </p>
        </div>
        <Button 
          onClick={() => openDialog()} 
          className="bg-primary hover:bg-primary/90 text-white"
          data-testid="add-servizio-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuovo Servizio
        </Button>
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
        <p className="text-sm text-blue-400">
          <strong>Nota:</strong> Ogni servizio di delivery genera automaticamente 2 colonne nella tabella Cassa & Contabilità: 
          una per i pagamenti <strong>POS</strong> e una per i pagamenti <strong>CASH</strong>.
        </p>
      </div>

      {/* Lista Servizi */}
      <div className="grid gap-3">
        {servizi.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Truck className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Nessun servizio delivery configurato</p>
            <p className="text-sm mt-2">Aggiungi servizi come Deliveroo, Glovo, Just Eat, ecc.</p>
          </div>
        ) : (
          servizi.map((item) => (
            <div
              key={item.id}
              className={`bg-card border rounded-2xl p-4 transition-colors ${
                item.attivo ? 'border-border' : 'border-border/50 opacity-60'
              }`}
              data-testid={`servizio-card-${item.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    item.attivo ? 'bg-orange-400/10' : 'bg-muted'
                  }`}>
                    <Truck className={`h-6 w-6 ${item.attivo ? 'text-orange-400' : 'text-muted-foreground'}`} />
                  </div>
                  
                  {/* Info */}
                  <div>
                    <h3 className="font-semibold text-foreground">{item.nome}</h3>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                      <span className="flex items-center gap-1">
                        <Percent className="h-3 w-3" />
                        Commissione: {item.commissione_percentuale}%
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        item.attivo 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {item.attivo ? 'Attivo' : 'Disattivato'}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Colonne: <span className="text-foreground">{item.nome}</span> (POS) + <span className="text-foreground">{item.nome} CASH</span>
                    </p>
                    {(() => {
                      const st = (item.storico_commissioni || []).slice().sort((a, b) => a.dal.localeCompare(b.dal));
                      if (st.length <= 1) return null;
                      return (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`timeline-${item.id}`}>
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          {st.map((h, i) => (
                            <span key={i} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-muted">
                              <span className="font-semibold text-foreground">{h.percentuale}%</span>
                              <span className="text-muted-foreground">{h.dal === "2000-01-01" ? "iniziale" : `dal ${formatShort(h.dal)}`}</span>
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openStorico(item)}
                    title="Storico commissioni"
                    data-testid={`storico-btn-${item.id}`}
                  >
                    <History className="h-4 w-4 text-indigo-400" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => toggleAttivo(item)}
                    title={item.attivo ? "Disattiva" : "Attiva"}
                  >
                    {item.attivo ? (
                      <ToggleRight className="h-5 w-5 text-green-400" />
                    ) : (
                      <ToggleLeft className="h-5 w-5 text-muted-foreground" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openDialog(item)}
                  >
                    <Pencil className="h-4 w-4 text-muted-foreground" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Trash2 className="h-4 w-4 text-red-400" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Dialog Crea/Modifica */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Truck className="h-5 w-5 text-primary" />
              {editItem ? "Modifica Servizio" : "Nuovo Servizio Delivery"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground/70">Nome Servizio *</Label>
              <Input
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="es. Deliveroo, Glovo, Just Eat..."
                className="bg-card border-border text-foreground"
                required
              />
              <p className="text-xs text-muted-foreground">
                Verranno create 2 colonne: "{form.nome || 'Nome'}" e "{form.nome || 'Nome'} CASH"
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground/70">Commissione (%)</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={form.commissione_percentuale}
                onChange={(e) => setForm({ ...form, commissione_percentuale: e.target.value })}
                placeholder="es. 30"
                className="bg-card border-border text-foreground"
              />
              <p className="text-xs text-muted-foreground">
                La commissione viene applicata sia ai pagamenti POS che CASH
              </p>
            </div>

            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-xl">
              <div>
                <Label className="text-foreground">Servizio Attivo</Label>
                <p className="text-xs text-muted-foreground">
                  I servizi disattivati non appaiono nella tabella
                </p>
              </div>
              <Switch
                checked={form.attivo}
                onCheckedChange={(checked) => setForm({ ...form, attivo: checked })}
              />
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="border-border text-foreground hover:bg-accent hover:text-accent-foreground"
              >
                Annulla
              </Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90 text-white">
                {editItem ? "Salva" : "Crea"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Storico Commissioni */}
      <Dialog open={storicoDialogOpen} onOpenChange={setStoricoDialogOpen}>
        <DialogContent className="max-w-md bg-card border-border" data-testid="storico-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <History className="h-5 w-5 text-indigo-500" />
              Storico commissioni — {storicoItem?.nome}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Ogni percentuale vale dalla data indicata in poi. Correggi date/valori se necessario. La riga "dall'inizio" copre tutte le date passate.
            </p>
            <div className="space-y-2">
              {storicoRows.map((r, idx) => {
                const isBaseline = r.dal === "2000-01-01";
                return (
                  <div key={idx} className="flex items-end gap-2 p-2.5 rounded-xl bg-muted/40 border border-border" data-testid={`storico-row-${idx}`}>
                    <div className="w-20">
                      <Label className="text-[11px] text-muted-foreground">%</Label>
                      <Input
                        type="number" step="0.1" min="0"
                        value={r.percentuale}
                        onChange={(e) => updateStoricoRow(idx, "percentuale", e.target.value)}
                        className="h-9 bg-card border-border text-foreground"
                        data-testid={`storico-perc-${idx}`}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label className="text-[11px] text-muted-foreground">{isBaseline ? "Validità" : "Valida dal"}</Label>
                      {isBaseline ? (
                        <div className="h-9 flex items-center text-sm text-muted-foreground px-1">dall'inizio</div>
                      ) : (
                        <DateInput value={r.dal} onChange={(d) => updateStoricoRow(idx, "dal", d)} />
                      )}
                    </div>
                    <Button
                      type="button" variant="ghost" size="icon"
                      className="h-9 w-9 shrink-0"
                      disabled={storicoRows.length <= 1}
                      onClick={() => removeStoricoRow(idx)}
                      data-testid={`storico-remove-${idx}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </Button>
                  </div>
                );
              })}
            </div>
            <Button
              type="button" variant="outline"
              className="w-full border-dashed border-border text-foreground hover:bg-accent"
              onClick={addStoricoRow}
              data-testid="storico-add-row"
            >
              <Plus className="h-4 w-4 mr-2" />Aggiungi variazione
            </Button>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setStoricoDialogOpen(false)} className="border-border text-foreground hover:bg-accent">
              Annulla
            </Button>
            <Button type="button" onClick={saveStorico} disabled={savingStorico} className="bg-primary hover:bg-primary/90 text-white" data-testid="storico-save-btn">
              {savingStorico ? "Salvataggio…" : "Salva storico"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiziDelivery;
